/**
 * DOM-Based Input System
 *
 * Connects the input system to actual DOM events for use in browsers.
 */

import type {
	Platform,
	Browser,
	LogLevel,
	Modifiers,
	InputContext,
	ZoomContext,
	PanContext,
	Handler,
	PlatformShortcut,
	HandlerOptions,
	ShortcutInfo,
	CommandInfo,
	ModeOptions,
	ModeEntry,
	DebugState,
	ZoomConfig,
	PanConfig,
	ParsedSequence,
	RegisteredHandler,
} from './types';

import { createModeStack, type ModeStack } from './modes';
import { createRegistry, type Registry } from './registry';
import {
	parseShortcut,
	matchesShortcut,
	isModifierKey,
	normalizeKey,
	createSequenceState,
	resetSequence,
	advanceSequence,
	type SequenceState,
} from './matcher';
import { shortcutToDisplay, resolvePlatformShortcut, getModKey, modifiersMatch } from './platform';
import {
	createZoomCalculator,
	createPanCalculator,
	type ZoomCalculator,
	type PanCalculator,
} from './gestures';
import {
	createDeviceModeDetector,
	type DeviceModeDetector,
	type InputDeviceMode,
} from './device-mode';

// ============================================================================
// INPUT SYSTEM INTERFACE
// ============================================================================

export interface InputSystem {
	// Registration - multiple overloads
	on(event: string, handler: Handler): () => void;
	on(event: string, shortcut: string | PlatformShortcut, handler: Handler): () => void;
	on(event: string, options: HandlerOptions, handler: Handler): () => void;
	on(
		event: string,
		shortcut: string | PlatformShortcut,
		options: HandlerOptions,
		handler: Handler
	): () => void;

	// Mode management
	pushMode(name: string, options?: ModeOptions): void;
	popMode(name: string): void;
	isMode(name: string): boolean;
	getStack(): string[];

	// State queries
	getModifiers(): Modifiers;
	getDebugState(): DebugState;
	/** @deprecated Use getCommands() instead */
	getShortcuts(): ShortcutInfo[];

	// Command system
	/** Get all registered commands */
	getCommands(): CommandInfo[];
	/** Get a specific command by ID */
	getCommand(commandId: string): CommandInfo | undefined;
	/** Execute a command by ID, returns true if command was found and executed */
	executeCommand(commandId: string): boolean;
	/** Set or clear the shortcut for a command */
	setShortcut(commandId: string, shortcut: string | null): void;

	// Configuration
	configureZoom(config: ZoomConfig): void;
	configurePan(config: PanConfig): void;
	setLogLevel(level: LogLevel): void;
	getPlatform(): Platform;
	getBrowser(): Browser;
	getZoomConfig(): ZoomConfig;
	getPanConfig(): PanConfig;

	// DOM connection
	connect(element: HTMLElement): () => void;

	// Svelte integration
	wrap<E extends Event>(handler: (event: E) => void): (event: E) => void;
}

export interface InputSystemOptions {
	platform?: Platform;
	browser?: Browser;
	logLevel?: LogLevel;
}

// ============================================================================
// PLATFORM DETECTION
// ============================================================================

function detectPlatform(): Platform {
	if (typeof navigator === 'undefined') return 'windows';
	const platform = navigator.platform?.toLowerCase() || '';
	if (platform.includes('mac')) return 'mac';
	if (platform.includes('linux')) return 'linux';
	return 'windows';
}

function detectBrowser(): Browser {
	if (typeof navigator === 'undefined') return 'chrome';
	const ua = navigator.userAgent.toLowerCase();
	if (ua.includes('safari') && !ua.includes('chrome')) return 'safari';
	if (ua.includes('firefox')) return 'firefox';
	if (ua.includes('edg')) return 'edge';
	return 'chrome';
}

// ============================================================================
// INPUT SYSTEM IMPLEMENTATION
// ============================================================================

export function createInputSystem(options?: InputSystemOptions): InputSystem {
	const platform: Platform = options?.platform ?? detectPlatform();
	const browser: Browser = options?.browser ?? detectBrowser();
	let logLevel: LogLevel = options?.logLevel ?? 'warn';

	// Core components
	const modeStack: ModeStack = createModeStack(logLevel);
	const registry: Registry = createRegistry(platform, logLevel);
	const sequenceState: SequenceState = createSequenceState();
	const deviceModeDetector: DeviceModeDetector = createDeviceModeDetector(platform, browser);
	const zoomCalculator: ZoomCalculator = createZoomCalculator(platform, browser);
	const panCalculator: PanCalculator = createPanCalculator(platform, browser);

	// State
	let currentModifiers: Modifiers = {
		ctrl: false,
		alt: false,
		shift: false,
		meta: false,
		mod: false,
	};
	let lastMousePosition = { x: 0, y: 0 };
	let isMiddleMouseDown = false;

	// ========================================================================
	// HELPER FUNCTIONS
	// ========================================================================

	function log(level: LogLevel, message: string): void {
		if (logLevel === 'silent') return;
		const levels: LogLevel[] = ['silent', 'warn', 'info', 'debug'];
		if (levels.indexOf(level) <= levels.indexOf(logLevel)) {
			if (level === 'warn') {
				console.warn(`[input] ${message}`);
			} else {
				console.log(`[input] ${message}`);
			}
		}
	}

	function updateModifiersFromEvent(event: KeyboardEvent | MouseEvent | WheelEvent): Modifiers {
		currentModifiers = {
			ctrl: event.ctrlKey,
			alt: event.altKey,
			shift: event.shiftKey,
			meta: event.metaKey,
			mod: platform === 'mac' ? event.metaKey : event.ctrlKey,
		};
		return currentModifiers;
	}

	function getModePosition(modeName: string): number {
		const entries = modeStack.getEntries();
		const index = entries.findIndex((e) => e.name === modeName);
		return index === -1 ? -1 : index;
	}

	function handlerModeMatches(handler: RegisteredHandler): boolean {
		const handlerMode = handler.options.mode ?? 'default';
		const excludeModes = handler.options.excludeModes;

		if (excludeModes && excludeModes.some((m) => modeStack.isMode(m))) {
			return false;
		}

		if (handlerMode === '*') {
			return true;
		}

		if (excludeModes && !handler.options.mode) {
			return true;
		}

		const topMode = modeStack.getTopMode();
		const modeEntries = modeStack.getEntries();

		if (handlerMode === topMode.name) {
			return true;
		}

		const handlerModeIndex = modeEntries.findIndex((e) => e.name === handlerMode);
		if (handlerModeIndex === -1) {
			return false;
		}

		for (let i = modeEntries.length - 1; i > handlerModeIndex; i--) {
			if (!modeEntries[i].overlay) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Check if a handler's mode matches using a snapshot of mode entries.
	 * This is used during event dispatch to prevent handlers in newly-pushed modes
	 * from firing for the same event that triggered the mode push.
	 */
	function handlerModeMatchesSnapshot(handler: RegisteredHandler, originalModeEntries: ModeEntry[]): boolean {
		const handlerMode = handler.options.mode ?? 'default';
		const excludeModes = handler.options.excludeModes;

		// Check exclude modes against the original snapshot
		if (excludeModes && excludeModes.some((m) => originalModeEntries.some((e) => e.name === m))) {
			return false;
		}

		if (handlerMode === '*') {
			return true;
		}

		if (excludeModes && !handler.options.mode) {
			return true;
		}

		const topMode = originalModeEntries[originalModeEntries.length - 1];

		if (handlerMode === topMode.name) {
			return true;
		}

		const handlerModeIndex = originalModeEntries.findIndex((e) => e.name === handlerMode);
		if (handlerModeIndex === -1) {
			return false;
		}

		// Check if only overlay modes are above the handler's mode
		for (let i = originalModeEntries.length - 1; i > handlerModeIndex; i--) {
			if (!originalModeEntries[i].overlay) {
				return false;
			}
		}

		return true;
	}

	// ========================================================================
	// EVENT DISPATCHERS
	// ========================================================================

	function dispatchKeydown(event: KeyboardEvent): void {
		const key = normalizeKey(event.key);
		const mods = updateModifiersFromEvent(event);
		const handlers = registry.getHandlers('keydown');
		let stopped = false;

		const originalModeEntries = modeStack.getEntries();
		const originalModes = new Set(originalModeEntries.map((e) => e.name));

		const sortedHandlers = [...handlers].sort((a, b) => {
			const aModePos = getModePosition(a.options.mode ?? 'default');
			const bModePos = getModePosition(b.options.mode ?? 'default');
			if (bModePos !== aModePos) return bModePos - aModePos;
			if (b.priority !== a.priority) return b.priority - a.priority;
			return a.id - b.id;
		});

		const matchingHandlers: RegisteredHandler[] = [];

		for (const handler of sortedHandlers) {
			const handlerMode = handler.options.mode ?? 'default';
			if (!originalModes.has(handlerMode) && handlerMode !== '*') {
				continue;
			}

			if (!handlerModeMatchesSnapshot(handler, originalModeEntries)) {
				continue;
			}

			if (handler.parsedSequence) {
				const sequence = handler.parsedSequence;

				if (sequence.parts.length > 1) {
					const result = advanceSequence(
						sequenceState,
						key,
						mods,
						sequence,
						platform,
						Date.now()
					);

					if (result === 'matched') {
						matchingHandlers.push(handler);
						resetSequence(sequenceState);
					} else if (result === 'partial') {
						event.preventDefault();
						return;
					} else if (sequenceState.position === 0) {
						const firstPart = sequence.parts[0];
						if (matchesShortcut(key, mods, firstPart, platform)) {
							if (sequence.parts.length === 1) {
								matchingHandlers.push(handler);
							} else {
								sequenceState.sequence = sequence;
								sequenceState.position = 1;
								sequenceState.startTime = Date.now();
								sequenceState.pendingDisplay = key;
								event.preventDefault();
								return;
							}
						}
					}
				} else {
					const part = sequence.parts[0];
					if (matchesShortcut(key, mods, part, platform)) {
						matchingHandlers.push(handler);
					}
				}
			} else {
				// No shortcut - matches all keydown events
				// Exception: if this is a command with cleared shortcut, don't match
				if (!handler.commandId) {
					matchingHandlers.push(handler);
				}
			}
		}

		if (sequenceState.position > 0 && matchingHandlers.length === 0) {
			resetSequence(sequenceState);
		}

		for (const handler of matchingHandlers) {
			if (stopped) break;

			const ctx: InputContext = {
				event,
				target: event.target as Element,
				modifiers: { ...mods },
				stopPropagation() {
					stopped = true;
					event.stopPropagation();
					event.preventDefault();
				},
			};

			try {
				handler.handler(ctx);
				log('debug', `Handler fired for ${handler.shortcut || 'keydown'}`);
			} catch (e) {
				console.error('Handler error:', e);
			}
		}
	}

	function dispatchKeyup(event: KeyboardEvent): void {
		const key = normalizeKey(event.key);
		updateModifiersFromEvent(event);

		const handlers = registry.getHandlers('keyup');

		const sortedHandlers = [...handlers].sort((a, b) => {
			const aModePos = getModePosition(a.options.mode ?? 'default');
			const bModePos = getModePosition(b.options.mode ?? 'default');
			if (bModePos !== aModePos) return bModePos - aModePos;
			if (b.priority !== a.priority) return b.priority - a.priority;
			return a.id - b.id;
		});

		for (const handler of sortedHandlers) {
			if (!handlerModeMatches(handler)) {
				continue;
			}

			if (handler.parsedSequence) {
				const part = handler.parsedSequence.parts[0];
				if (!matchesShortcut(key, currentModifiers, part, platform)) {
					continue;
				}
			}

			const ctx: InputContext = {
				event,
				target: event.target as Element,
				modifiers: { ...currentModifiers },
				stopPropagation() {
					event.stopPropagation();
				},
			};

			try {
				handler.handler(ctx);
			} catch (e) {
				console.error('Handler error:', e);
			}
		}
	}

	function dispatchMouseEvent(eventType: string, event: MouseEvent): void {
		const mods = updateModifiersFromEvent(event);
		const position = { x: event.clientX, y: event.clientY };
		const delta = {
			x: event.clientX - lastMousePosition.x,
			y: event.clientY - lastMousePosition.y,
		};
		lastMousePosition = position;

		if (eventType === 'mousedown' && event.button === 1) {
			isMiddleMouseDown = true;
		}
		if (eventType === 'mouseup' && event.button === 1) {
			isMiddleMouseDown = false;
		}

		// Handle MMB pan
		if (eventType === 'mousemove' && isMiddleMouseDown) {
			const panResult = panCalculator.calculate(delta.x, delta.y, 'mouse', false);
			dispatchPan({
				event,
				target: event.target as Element,
				modifiers: { ...mods },
				delta: panResult.delta,
				source: panResult.source,
				inertial: panResult.inertial,
				stopPropagation() {
					event.stopPropagation();
				},
			});
		}

		const handlers = registry.getHandlers(eventType);
		let stopped = false;

		const originalModeEntries = modeStack.getEntries();
		const originalModes = new Set(originalModeEntries.map((e) => e.name));

		const sortedHandlers = [...handlers].sort((a, b) => {
			const aModePos = getModePosition(a.options.mode ?? 'default');
			const bModePos = getModePosition(b.options.mode ?? 'default');
			if (bModePos !== aModePos) return bModePos - aModePos;
			if (b.priority !== a.priority) return b.priority - a.priority;
			return a.id - b.id;
		});

		for (const handler of sortedHandlers) {
			if (stopped) break;

			const handlerMode = handler.options.mode ?? 'default';
			if (!originalModes.has(handlerMode) && handlerMode !== '*') {
				continue;
			}

			if (!handlerModeMatchesSnapshot(handler, originalModeEntries)) {
				continue;
			}

			const ctx: InputContext = {
				event,
				target: event.target as Element,
				modifiers: { ...mods },
				position,
				delta,
				button: event.button,
				stopPropagation() {
					stopped = true;
					event.stopPropagation();
				},
			};

			try {
				handler.handler(ctx);
			} catch (e) {
				console.error('Handler error:', e);
			}
		}
	}

	// ========================================================================
	// WHEEL EVENT HANDLING (Mode-based detection)
	// ========================================================================
	//
	// Uses a state machine approach: maintain an assumed device mode (mouse/trackpad)
	// and respond immediately within that mode. Higher threshold to switch modes
	// prevents flip-flopping while allowing immediate response.

	function dispatchWheel(event: WheelEvent): void {
		const mods = updateModifiersFromEvent(event);
		const position = { x: event.clientX, y: event.clientY };

		// Prevent default early to stop browser zoom/scroll
		event.preventDefault();

		// Process event through mode detector
		const result = deviceModeDetector.processEvent({
			deltaX: event.deltaX,
			deltaY: event.deltaY,
			ctrlKey: event.ctrlKey,
			timestamp: Date.now(),
		});

		if (result.action === 'zoom') {
			const zoomResult = zoomCalculator.calculate(event.deltaY, position, result.source);
			dispatchZoom({
				event,
				target: event.target as Element,
				modifiers: { ...mods },
				factor: zoomResult.factor,
				origin: zoomResult.origin,
				source: result.source,
				// Include raw deltaY for mouse wheel - enables smooth animation via ZoomAnimator
				deltaY: result.source === 'mouse' ? event.deltaY : undefined,
				stopPropagation() {
					event.stopPropagation();
					event.preventDefault();
				},
			});
		} else {
			const panResult = panCalculator.calculate(event.deltaX, event.deltaY, result.source, false);
			dispatchPan({
				event,
				target: event.target as Element,
				modifiers: { ...mods },
				delta: panResult.delta,
				source: result.source,
				inertial: panResult.inertial,
				stopPropagation() {
					event.stopPropagation();
					event.preventDefault();
				},
			});
		}
	}

	function dispatchZoom(ctx: ZoomContext): void {
		const handlers = registry.getHandlers('zoom');
		let stopped = false;

		for (const handler of handlers) {
			if (stopped) break;

			if (!handlerModeMatches(handler)) {
				continue;
			}

			try {
				handler.handler({
					...ctx,
					stopPropagation() {
						stopped = true;
						ctx.stopPropagation();
					},
				});
			} catch (e) {
				console.error('Handler error:', e);
			}
		}
	}

	function dispatchPan(ctx: PanContext): void {
		const handlers = registry.getHandlers('pan');
		let stopped = false;

		for (const handler of handlers) {
			if (stopped) break;

			if (!handlerModeMatches(handler)) {
				continue;
			}

			try {
				handler.handler({
					...ctx,
					stopPropagation() {
						stopped = true;
						ctx.stopPropagation();
					},
				});
			} catch (e) {
				console.error('Handler error:', e);
			}
		}
	}

	// ========================================================================
	// PUBLIC API
	// ========================================================================

	const inputSystem: InputSystem = {
		on(...args: any[]): () => void {
			let eventType: string;
			let shortcut: string | PlatformShortcut | null = null;
			let options: HandlerOptions = {};
			let handler: Handler;

			if (args.length === 2) {
				eventType = args[0];
				handler = args[1];
			} else if (args.length === 3) {
				eventType = args[0];
				if (typeof args[1] === 'function') {
					throw new Error('Invalid arguments');
				} else if (typeof args[1] === 'string') {
					shortcut = args[1];
					handler = args[2];
				} else if (
					'mac' in args[1] ||
					'windows' in args[1] ||
					'linux' in args[1] ||
					'default' in args[1]
				) {
					shortcut = args[1];
					handler = args[2];
				} else {
					options = args[1] as HandlerOptions;
					handler = args[2];
				}
			} else if (args.length === 4) {
				eventType = args[0];
				shortcut = args[1];
				options = args[2];
				handler = args[3];
			} else {
				throw new Error('Invalid arguments to on()');
			}

			const resolvedShortcut = shortcut
				? typeof shortcut === 'string'
					? shortcut
					: resolvePlatformShortcut(shortcut, platform)
				: null;

			return registry.register(eventType, resolvedShortcut, options, handler);
		},

		pushMode(name: string, opts?: ModeOptions): void {
			modeStack.pushMode(name, opts);
			log('info', `Pushed mode: ${name}`);
		},

		popMode(name: string): void {
			modeStack.popMode(name);
			log('info', `Popped mode: ${name}`);
		},

		isMode(name: string): boolean {
			return modeStack.isMode(name);
		},

		getStack(): string[] {
			return modeStack.getStack();
		},

		getModifiers(): Modifiers {
			return { ...currentModifiers };
		},

		getDebugState(): DebugState {
			return {
				stack: modeStack.getStack(),
				modifiers: { ...currentModifiers },
				pendingSequence: sequenceState.pendingDisplay,
				deviceMode: deviceModeDetector.getMode(),
			};
		},

		getShortcuts(): ShortcutInfo[] {
			return registry.getShortcuts().map((s) => ({
				...s,
				display: shortcutToDisplay(s.shortcut, platform),
			}));
		},

		getCommands(): CommandInfo[] {
			return registry.getCommands();
		},

		getCommand(commandId: string): CommandInfo | undefined {
			return registry.getCommand(commandId);
		},

		executeCommand(commandId: string): boolean {
			return registry.executeCommand(commandId);
		},

		setShortcut(commandId: string, shortcut: string | null): void {
			registry.setShortcut(commandId, shortcut);
		},

		configureZoom(config: ZoomConfig): void {
			zoomCalculator.configure(config);
		},

		configurePan(config: PanConfig): void {
			panCalculator.configure(config);
		},

		setLogLevel(level: LogLevel): void {
			logLevel = level;
			modeStack.setLogLevel(level);
			registry.setLogLevel(level);
		},

		getPlatform(): Platform {
			return platform;
		},

		getBrowser(): Browser {
			return browser;
		},

		getZoomConfig(): ZoomConfig {
			return zoomCalculator.getConfig();
		},

		getPanConfig(): PanConfig {
			return panCalculator.getConfig();
		},

		connect(element: HTMLElement): () => void {
			const keydownHandler = (e: KeyboardEvent) => dispatchKeydown(e);
			const keyupHandler = (e: KeyboardEvent) => dispatchKeyup(e);
			const mousedownHandler = (e: MouseEvent) => dispatchMouseEvent('mousedown', e);
			const mouseupHandler = (e: MouseEvent) => dispatchMouseEvent('mouseup', e);
			const mousemoveHandler = (e: MouseEvent) => dispatchMouseEvent('mousemove', e);
			const clickHandler = (e: MouseEvent) => dispatchMouseEvent('click', e);
			const dblclickHandler = (e: MouseEvent) => dispatchMouseEvent('dblclick', e);
			const contextmenuHandler = (e: MouseEvent) => dispatchMouseEvent('contextmenu', e);
			const wheelHandler = (e: WheelEvent) => dispatchWheel(e);

			// Touch events signal trackpad usage - helps disambiguate device mode
			// Only use on non-Mac platforms since Mac trackpads always fire touch events
			// which interferes with the wheel event heuristics (especially in Safari)
			const touchHandler =
				platform !== 'mac'
					? () => {
							deviceModeDetector.onTouchEvent();
						}
					: null;

			element.addEventListener('keydown', keydownHandler);
			element.addEventListener('keyup', keyupHandler);
			element.addEventListener('mousedown', mousedownHandler);
			element.addEventListener('mouseup', mouseupHandler);
			element.addEventListener('mousemove', mousemoveHandler);
			element.addEventListener('click', clickHandler);
			element.addEventListener('dblclick', dblclickHandler);
			element.addEventListener('contextmenu', contextmenuHandler);
			element.addEventListener('wheel', wheelHandler, { passive: false });
			if (touchHandler) {
				element.addEventListener('touchstart', touchHandler, { passive: true });
				element.addEventListener('touchmove', touchHandler, { passive: true });
			}

			// Make element focusable for keyboard events
			if (!element.hasAttribute('tabindex')) {
				element.setAttribute('tabindex', '0');
			}

			log('info', 'Connected to element');

			return () => {
				element.removeEventListener('keydown', keydownHandler);
				element.removeEventListener('keyup', keyupHandler);
				element.removeEventListener('mousedown', mousedownHandler);
				element.removeEventListener('mouseup', mouseupHandler);
				element.removeEventListener('mousemove', mousemoveHandler);
				element.removeEventListener('click', clickHandler);
				element.removeEventListener('dblclick', dblclickHandler);
				element.removeEventListener('contextmenu', contextmenuHandler);
				element.removeEventListener('wheel', wheelHandler);
				if (touchHandler) {
					element.removeEventListener('touchstart', touchHandler);
					element.removeEventListener('touchmove', touchHandler);
				}
				log('info', 'Disconnected from element');
			};
		},

		wrap<E extends Event>(handler: (event: E) => void): (event: E) => void {
			return (event: E) => {
				const topMode = modeStack.getTopMode();
				if (!topMode.overlay && topMode.name !== 'default') {
					return;
				}
				handler(event);
			};
		},
	};

	return inputSystem;
}
